/* eslint-disable react-hooks/exhaustive-deps */
import { useTranslation } from "react-i18next";
import { CircularProgress, Dialog, Typography } from "@material-ui/core";
import BoxSync from "pages/Integrations/Applications/BoxSync";
import OnedriveSync from "pages/Integrations/Applications/OnedriveSync";
import GappsSync from "pages/Integrations/Applications/GappsSync";
import {
  SyncDialogCloseButton,
  SyncDialogContent,
  SyncDialogTitleContainer,
} from "./styled";
import { StorageSettingsUpdate } from "pages/Integrations/types";
import { StorageAccountItem } from "../types";
import { StorageSettings } from "@dashboard-v3/api";
import { useFetch } from "utils/api/useFetch";

interface Props {
  isOpen: boolean;
  selectedAccount: StorageAccountItem;
  onClose: () => void;
  onChange: () => void;
}

interface DialogHeader {
  title: string;
  subtitle: string;
  onClose: Props["onClose"];
}

export default function SyncModal(props: Props) {
  const { isOpen, selectedAccount, onClose, onChange } = props;
  const { t } = useTranslation("components");
  const { storageAccount } = selectedAccount;
  const { provider } = storageAccount;

  const { data: settings, isLoading } =
    useFetch<StorageSettings>("/storages/settings");

  const handleOnAccountSync = (update: StorageSettingsUpdate) => {
    const providerKey =
      provider === "GOOGLEDRIVE" ? "gapps" : provider.toLowerCase();
    const { installed } = update[providerKey];

    if (installed) {
      onChange();
    }
  };

  if (isLoading) {
    return (
      <Dialog open={isOpen}>
        <SyncDialogContent style={{ height: "156px" }}>
          <CircularProgress style={{ alignSelf: "center" }} />
        </SyncDialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <SyncDialogContent>
        {provider === "BOX" && (
          <>
            <DialogTitle
              title={t("storageAccounts.syncModal.BOX.title")}
              subtitle={t("storageAccounts.syncModal.BOX.explanation")}
              onClose={onClose}
            />
            <BoxSync settings={settings} onAccountSync={handleOnAccountSync} />
          </>
        )}
        {provider === "ONEDRIVE" && (
          <>
            <DialogTitle
              title={t("storageAccounts.syncModal.ONEDRIVE.title")}
              subtitle={t("storageAccounts.syncModal.ONEDRIVE.explanation")}
              onClose={onClose}
            />
            <OnedriveSync
              settings={settings}
              onAccountSync={handleOnAccountSync}
            />
          </>
        )}
        {provider === "GOOGLEDRIVE" && (
          <>
            <DialogTitle
              title={t("storageAccounts.syncModal.GOOGLEDRIVE.title")}
              subtitle={t("storageAccounts.syncModal.GOOGLEDRIVE.explanation")}
              onClose={onClose}
            />
            <GappsSync
              settings={settings}
              onAccountSync={handleOnAccountSync}
            />
          </>
        )}
      </SyncDialogContent>
    </Dialog>
  );
}

const DialogTitle = ({ title, subtitle, onClose }: DialogHeader) => {
  return (
    <>
      <SyncDialogTitleContainer>
        <Typography variant="h6">{title}</Typography>
        <SyncDialogCloseButton onClick={onClose} />
      </SyncDialogTitleContainer>
      <Typography variant="caption" color="textSecondary">
        {subtitle}
      </Typography>
    </>
  );
};
